import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { apiService } from "../../../services/api";
import { DashboardObjetivo, CreateDashboardObjetivoData, TipoObjetivo } from "../../../types/dashboard";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale/pt-BR';

const route = all_routes;

// Corrigindo a importação do locale e capitalizando os meses
const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

const MONTHS_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez'
];

const customPtBR = {
  ...ptBR,
  localize: {
    ...ptBR.localize,
    month: (n: number) => MONTHS[n],
    monthShort: (n: number) => MONTHS_SHORT[n]
  }
};

registerLocale('pt-BR', customPtBR as any);

// Helper para formatar data no timezone local (evita problema de UTC)
const formatDateToLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper para criar Date a partir de string YYYY-MM-DD no timezone local
const createDateFromLocal = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  // Criar data no timezone local (mês é 0-indexed em JavaScript)
  return new Date(year, month - 1, day);
};

// Helper para formatar data para exibição (evita problema de UTC)
const formatDataParaExibicao = (dateString: string): string => {
  const data = createDateFromLocal(dateString);
  return data.toLocaleDateString('pt-BR');
};

const DashboardObjectives = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [objetivos, setObjetivos] = useState<DashboardObjetivo[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateDashboardObjetivoData>({
    tipo_objeto: 'faturamento_mensal',
    periodo: 'mensal',
    valor_objetivo: 0,
    data_inicio: formatDateToLocal(new Date()),
    data_fim: formatDateToLocal(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)) // Last day of current month
  });

  const fetchObjetivos = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDashboardObjetivos();
      if (response.success) {
        setObjetivos(response.data);
      } else {
        toast.error("Erro ao carregar objetivos");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar objetivos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObjetivos();
  }, []);

  const getAvailablePeriods = (tipo: TipoObjetivo): string[] => {
    if (tipo.includes('diario')) return ['diario'];
    if (tipo.includes('semanal')) return ['semanal'];
    if (tipo.includes('mensal')) return ['mensal'];
    if (tipo.includes('semestral')) return ['semestral'];
    if (tipo.includes('anual')) return ['anual'];
    return ['diario', 'semanal', 'mensal', 'semestral', 'anual'];
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'tipo_objeto') {
        const availablePeriods = getAvailablePeriods(value as TipoObjetivo);
        setFormData(prev => ({
            ...prev,
            [name]: value as TipoObjetivo,
            periodo: availablePeriods[0] // Auto-select the valid period
        }));
    } else {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_objeto: 'faturamento_mensal',
      periodo: 'mensal',
      valor_objetivo: 0,
      data_inicio: formatDateToLocal(new Date()),
      data_fim: formatDateToLocal(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0))
    });
    setEditingId(null);
  };

  const handleEdit = (objetivo: DashboardObjetivo) => {
    console.log("📝 CARREGANDO objetivo para edição:", objetivo);
    setFormData({
      tipo_objeto: objetivo.tipo_objeto,
      periodo: objetivo.periodo,
      valor_objetivo: objetivo.valor_objetivo,
      data_inicio: objetivo.data_inicio,
      data_fim: objetivo.data_fim
    });
    setEditingId(objetivo.id);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Tem certeza que deseja excluir este objetivo?")) {
      try {
        await apiService.deleteDashboardObjetivo(id);
        toast.success("Objetivo excluído com sucesso");
        fetchObjetivos();
      } catch (error) {
        toast.error("Erro ao excluir objetivo");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        const updateData = {
          valor_objetivo: formData.valor_objetivo,
          data_inicio: formData.data_inicio,
          data_fim: formData.data_fim
        };
        console.log("🔧 EDITANDO objetivo:", {
          id: editingId,
          dados: updateData,
          formDataCompleto: formData
        });
        await apiService.updateDashboardObjetivo(editingId, updateData);
        toast.success("Objetivo atualizado com sucesso");
      } else {
        console.log("➕ CRIANDO novo objetivo:", formData);
        await apiService.createDashboardObjetivo(formData);
        toast.success("Objetivo criado com sucesso");
      }
      setShowModal(false);
      resetForm();
      fetchObjetivos();
    } catch (error) {
      toast.error("Erro ao salvar objetivo");
      console.error(error);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-sm-4"><h4 className="page-title">Configurações de Dashboard</h4></div>
                <div className="col-sm-8 text-sm-end"><div className="head-icons"><CollapseHeader /></div></div>
              </div>
            </div>
            
            <div className="row">
              <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                <div className="card">
                  <div className="card-body">
                    <div className="settings-sidebar">
                      <h4 className="fw-semibold mb-3">Configurações</h4>
                      <div className="list-group list-group-flush settings-sidebar">
                        <Link to={route.dashboardObjectives} className="fw-medium active">Objetivos</Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-9 col-lg-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h4 className="fw-semibold mb-0">Objetivos e Metas</h4>
                    <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                      <i className="ti ti-plus me-1"/> Novo Objetivo
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                       <table className="table table-hover">
                         <thead>
                           <tr>
                             <th>Tipo</th>
                             <th>Valor</th>
                             <th>Período</th>
                             <th>Início</th>
                             <th>Fim</th>
                             <th>Ações</th>
                           </tr>
                         </thead>
                         <tbody>
                           {objetivos.map(obj => (
                             <tr key={obj.id}>
                               <td>{obj.tipo_objeto.replace(/_/g, ' ').toUpperCase()}</td>
                               <td>
                                 {obj.tipo_objeto.includes('faturamento') 
                                   ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(obj.valor_objetivo)
                                   : new Intl.NumberFormat('pt-BR').format(obj.valor_objetivo)}
                               </td>
                               <td>{obj.periodo}</td>
                               <td>{formatDataParaExibicao(obj.data_inicio)}</td>
                               <td>{formatDataParaExibicao(obj.data_fim)}</td>
                               <td>
                                 <button className="btn btn-sm btn-icon btn-light me-1" onClick={() => handleEdit(obj)}><i className="ti ti-edit"/></button>
                                 <button className="btn btn-sm btn-icon btn-light text-danger" onClick={() => handleDelete(obj.id)}><i className="ti ti-trash"/></button>
                               </td>
                             </tr>
                           ))}
                           {objetivos.length === 0 && (
                             <tr><td colSpan={6} className="text-center">Nenhum objetivo cadastrado.</td></tr>
                           )}
                         </tbody>
                       </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal / Form Overlay */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Editar Objetivo' : 'Novo Objetivo'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tipo de Objetivo</label>
                    <select className="form-select" name="tipo_objeto" value={formData.tipo_objeto} onChange={handleInputChange} disabled={!!editingId}>
                      {/* Faturamento */}
                      <option value="faturamento_diario">Faturamento Diário</option>
                      <option value="faturamento_mensal">Faturamento Mensal</option>
                      <option value="faturamento_semestral">Faturamento Semestral</option>
                      <option value="faturamento_anual">Faturamento Anual</option>

                      {/* Disputas */}
                      <option value="disputas_diario">Disputas (Diário)</option>
                      <option value="disputas_semanal">Disputas (Semanal)</option>
                      <option value="disputas_mensal">Disputas (Mensal)</option>
                      <option value="disputas_semestral">Disputas (Semestral)</option>
                      <option value="disputas_anual">Disputas (Anual)</option>

                      {/* Novos Oportunidades */}
                      <option value="novos_oportunidades_diario">Novas Oportunidades (Diário)</option>
                      <option value="novos_oportunidades_semanal">Novas Oportunidades (Semanal)</option>
                      <option value="novos_oportunidades_mensal">Novas Oportunidades (Mensal)</option>
                      <option value="novos_oportunidades_semestral">Novas Oportunidades (Semestral)</option>
                      <option value="novos_oportunidades_anual">Novas Oportunidades (Anual)</option>

                      {/* Oportunidades Perdidas */}
                      <option value="oportunidades_perdidas_diario">Oportunidades Perdidas (Diário)</option>
                      <option value="oportunidades_perdidas_semanal">Oportunidades Perdidas (Semanal)</option>
                      <option value="oportunidades_perdidas_mensal">Oportunidades Perdidas (Mensal)</option>
                      <option value="oportunidades_perdidas_semestral">Oportunidades Perdidas (Semestral)</option>
                      <option value="oportunidades_perdidas_anual">Oportunidades Perdidas (Anual)</option>

                      {/* Oportunidades Suspensas */}
                      <option value="oportunidades_suspensas_diario">Oportunidades Suspensas (Diário)</option>
                      <option value="oportunidades_suspensas_semanal">Oportunidades Suspensas (Semanal)</option>
                      <option value="oportunidades_suspensas_mensal">Oportunidades Suspensas (Mensal)</option>
                      <option value="oportunidades_suspensas_semestral">Oportunidades Suspensas (Semestral)</option>
                      <option value="oportunidades_suspensas_anual">Oportunidades Suspensas (Anual)</option>

                      {/* Oportunidades em Andamento */}
                      <option value="oportunidades_andamento_diario">Oportunidades em Andamento (Diário)</option>
                      <option value="oportunidades_andamento_semanal">Oportunidades em Andamento (Semanal)</option>
                      <option value="oportunidades_andamento_mensal">Oportunidades em Andamento (Mensal)</option>
                      <option value="oportunidades_andamento_semestral">Oportunidades em Andamento (Semestral)</option>
                      <option value="oportunidades_andamento_anual">Oportunidades em Andamento (Anual)</option>

                      {/* Vitórias */}
                      <option value="vitorias_diario">Vitórias (Diário)</option>
                      <option value="vitorias_semanal">Vitórias (Semanal)</option>
                      <option value="vitorias_mensal">Vitórias (Mensal)</option>
                      <option value="vitorias_semestral">Vitórias (Semestral)</option>
                      <option value="vitorias_anual">Vitórias (Anual)</option>

                      {/* Oportunidades em Monitoramento */}
                      <option value="oportunidades_monitoramento_diario">Em Monitoramento (Diário)</option>
                      <option value="oportunidades_monitoramento_semanal">Em Monitoramento (Semanal)</option>
                      <option value="oportunidades_monitoramento_mensal">Em Monitoramento (Mensal)</option>
                      <option value="oportunidades_monitoramento_semestral">Em Monitoramento (Semestral)</option>
                      <option value="oportunidades_monitoramento_anual">Em Monitoramento (Anual)</option>

                      {/* Oportunidades Canceladas */}
                      <option value="oportunidades_canceladas_diario">Canceladas (Diário)</option>
                      <option value="oportunidades_canceladas_semanal">Canceladas (Semanal)</option>
                      <option value="oportunidades_canceladas_mensal">Canceladas (Mensal)</option>
                      <option value="oportunidades_canceladas_semestral">Canceladas (Semestral)</option>
                      <option value="oportunidades_canceladas_anual">Canceladas (Anual)</option>

                      {/* Etapa do Funil: Novas Oportunidades */}
                      <option value="etapa_novas_oportunidades_diario">Etapa - Novas Oportunidades (Diário)</option>
                      <option value="etapa_novas_oportunidades_semanal">Etapa - Novas Oportunidades (Semanal)</option>
                      <option value="etapa_novas_oportunidades_mensal">Etapa - Novas Oportunidades (Mensal)</option>
                      <option value="etapa_novas_oportunidades_semestral">Etapa - Novas Oportunidades (Semestral)</option>
                      <option value="etapa_novas_oportunidades_anual">Etapa - Novas Oportunidades (Anual)</option>

                      {/* Etapa do Funil: Pré-Disputa */}
                      <option value="etapa_pre_disputa_diario">Etapa - Pré-Disputa (Diário)</option>
                      <option value="etapa_pre_disputa_semanal">Etapa - Pré-Disputa (Semanal)</option>
                      <option value="etapa_pre_disputa_mensal">Etapa - Pré-Disputa (Mensal)</option>
                      <option value="etapa_pre_disputa_semestral">Etapa - Pré-Disputa (Semestral)</option>
                      <option value="etapa_pre_disputa_anual">Etapa - Pré-Disputa (Anual)</option>

                      {/* Etapa do Funil: Disputa */}
                      <option value="etapa_disputa_diario">Etapa - Disputa (Diário)</option>
                      <option value="etapa_disputa_semanal">Etapa - Disputa (Semanal)</option>
                      <option value="etapa_disputa_mensal">Etapa - Disputa (Mensal)</option>
                      <option value="etapa_disputa_semestral">Etapa - Disputa (Semestral)</option>
                      <option value="etapa_disputa_anual">Etapa - Disputa (Anual)</option>

                      {/* Etapa do Funil: Habilitação */}
                      <option value="etapa_habilitacao_diario">Etapa - Habilitação (Diário)</option>
                      <option value="etapa_habilitacao_semanal">Etapa - Habilitação (Semanal)</option>
                      <option value="etapa_habilitacao_mensal">Etapa - Habilitação (Mensal)</option>
                      <option value="etapa_habilitacao_semestral">Etapa - Habilitação (Semestral)</option>
                      <option value="etapa_habilitacao_anual">Etapa - Habilitação (Anual)</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Valor do Objetivo</label>
                    <input type="number" className="form-control" name="valor_objetivo" value={formData.valor_objetivo} onChange={handleInputChange} step="0.01" required />
                  </div>
                  <div className="mb-3">
                     <label className="form-label">Período</label>
                     <select className="form-select" name="periodo" value={formData.periodo} onChange={handleInputChange}>
                       {getAvailablePeriods(formData.tipo_objeto).map(period => (
                           <option key={period} value={period}>
                               {period.charAt(0).toUpperCase() + period.slice(1)}
                           </option>
                       ))}
                     </select>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Data Início</label>
                        <DatePicker
                          className="form-control"
                          selected={createDateFromLocal(formData.data_inicio)}
                          onChange={(date) => {
                            if (date) {
                              setFormData(prev => ({
                                ...prev,
                                data_inicio: formatDateToLocal(date)
                              }));
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="dd/mm/aaaa"
                          locale="pt-BR"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Data Fim</label>
                        <DatePicker
                          className="form-control"
                          selected={createDateFromLocal(formData.data_fim)}
                          onChange={(date) => {
                            if (date) {
                              setFormData(prev => ({
                                ...prev,
                                data_fim: formatDateToLocal(date)
                              }));
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="dd/mm/aaaa"
                          locale="pt-BR"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary">Salvar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardObjectives;
